import { Fragment } from 'react';
import classNames from 'classnames';
import { useAdvanceTableContext } from '../../providers/AdvanceTableProvider';
import { Table } from 'react-bootstrap';
import { flexRender } from '@tanstack/react-table';

// ✅ helper safe: acceptă obiect sau funcție pentru meta props
function resolveMetaProps(metaProps, ctx) {
  if (!metaProps) return {};
  if (typeof metaProps === 'function') {
    const out = metaProps(ctx);
    return out && typeof out === 'object' ? out : {};
  }
  // obiect (comportament vechi)
  return metaProps;
}

const AdvanceTable = ({
  headerClassName,
  bodyClassName,
  rowClassName,
  tableProps,
  hasFooter,
  // Optional per-row props — a click handler, a highlight, a title. Absent by
  // default, so every existing table renders exactly as it did.
  getRowProps,
  // Optional extra row underneath. Return null (the default) and nothing is
  // drawn, so every existing table is untouched. Used for detail a row cannot
  // afford inline — a list of an order's cables, say, which would otherwise
  // either wrap the row to four lines or be truncated into uselessness.
  renderSubRow
}) => {
  const table = useAdvanceTableContext();
  const { getRowModel, getFlatHeaders, getFooterGroups } = table;

  return (
    <div className="scrollbar ms-n1 ps-1">
      <Table {...tableProps}>
        <thead className={headerClassName}>
          <tr>
            {getFlatHeaders().map(header => {
              const meta = header.column.columnDef.meta || {};
              const headerProps = resolveMetaProps(meta.headerProps, {
                header,
                column: header.column,
                table
              });

              return (
                <th
                  key={header.id}
                  {...headerProps}
                  className={classNames(headerProps?.className, {
                    sort: header.column.getCanSort(),
                    desc: header.column.getIsSorted() === 'desc',
                    asc: header.column.getIsSorted() === 'asc'
                  })}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody className={bodyClassName}>
          {getRowModel().rows.map(row => {
            const sub = typeof renderSubRow === 'function' ? renderSubRow(row) : null;
            const cells = row.getVisibleCells();

            return (
              <Fragment key={row.id}>
                <tr
                  className={rowClassName}
                  {...(typeof getRowProps === 'function' ? getRowProps(row) : {})}
                >
                  {cells.map(cell => {
                    const meta = cell.column.columnDef.meta || {};
                    const cellProps = resolveMetaProps(meta.cellProps, {
                      cell,
                      row: cell.row,
                      column: cell.column,
                      table
                    });

                    return (
                      <td key={cell.id} {...cellProps}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>

                {sub && (
                  <tr className="bg-body-highlight">
                    {/* Spans the visible cells, not the column definitions —
                        a hidden column would otherwise leave the panel short
                        and the table visibly ragged. */}
                    <td colSpan={cells.length} className="p-0 border-top-0">
                      {sub}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>

        {hasFooter && (
          <tfoot>
            {getFooterGroups().map(footerGroup => (
              <tr key={footerGroup.id} className="border-0">
                {footerGroup.headers.map(header => {
                  const meta = header.column.columnDef.meta || {};
                  const footerProps = resolveMetaProps(meta.footerProps, {
                    header,
                    column: header.column,
                    table
                  });

                  return (
                    <th key={header.id} {...footerProps}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                          )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </tfoot>
        )}
      </Table>
    </div>
  );
};

export default AdvanceTable;
